import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  ArrowPathIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { addTicketsToBasket, removeTicketsFromBasket } from '@lib/spektrix/index'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix-fetcher'
import { useTicketTypes } from '@lib/use-ticket-types'
import BasketUpdateEvent from '@lib/basket-update-event'

const MealDeals = ({ instance, mainTicketsInBasket, mutate, showBeforeBasket = false, onMealDealsChange, model }) => {
  const [updatingMealDeals, setUpdatingMealDeals] = useState(false)
  const [selectedMealDeals, setSelectedMealDeals] = useState(0)
  const [pendingRemoval, setPendingRemoval] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Get meal deals from instance data
  const mealDeals = instance.meal_deals || []
  // Filter out meal deals with no availability or past stop_selling_at
  const availableMealDeals = mealDeals.filter(md => {
    // Check availability
    if (md.available <= 0) return false

    // Check stop_selling_at
    if (md.stop_selling_at) {
      const stopSellingTime = new Date(md.stop_selling_at)
      if (stopSellingTime <= new Date()) return false
    }

    return true
  })
  const mealDeal = availableMealDeals[0] // Get the first available meal deal

  // All hooks must be called unconditionally and in the same order every render
  const { data: mealDealPlan } = useSWR(
    mealDeal ? `/instances/${mealDeal.spektrix_v3_id || mealDeal.id}/plan?$expand=2` : null,
    SpektrixFetcher
  )

  const mealDealTicketTypes = useTicketTypes(mealDeal ? (mealDeal.spektrix_v3_id || mealDeal.id) : null)

  // Fetch current basket to see meal deal tickets
  const { data: basketContents, mutate: mutateBasket } = useSWR('/basket', SpektrixFetcher)

  // Listen for basket update events
  useEffect(() => {
    const handleBasketUpdate = () => {
      mutateBasket()
    }
    window.addEventListener(BasketUpdateEvent, handleBasketUpdate)
    return () => {
      window.removeEventListener(BasketUpdateEvent, handleBasketUpdate)
    }
  }, [mutateBasket])

  // Get ALL meal deal tickets in basket (not filtered by current instance)
  const allMealDealsInBasket = useMemo(() => {
    if (!basketContents?.tickets) return []

    return basketContents.tickets.filter(t => {
      const eventName = t.event?.name || ''
      return eventName.toLowerCase().includes('dinner offer') ||
             eventName.toLowerCase().includes('meal deal')
    })
  }, [basketContents])
  
  // Get meal deal tickets that match THIS instance's available meal deals
  const instanceMealDealsInBasket = useMemo(() => {
    if (!basketContents?.tickets || availableMealDeals.length === 0) return []
    
    const mealDealInstanceIds = availableMealDeals.flatMap(md => [
      md.spektrix_v3_id,
      md.id,
      String(md.spektrix_v3_id),
      String(md.id)
    ].filter(Boolean))
    
    return basketContents.tickets.filter(t => {
      const ticketInstanceId = t.instance?.id || t.instance_id
      return mealDealInstanceIds.includes(ticketInstanceId) ||
             mealDealInstanceIds.includes(String(ticketInstanceId))
    })
  }, [basketContents, availableMealDeals])

  // Calculate values
  const totalMainTickets = mainTicketsInBasket?.length || 0
  
  // Determine which meal deals to use for display
  // If we have main tickets for this event, show instance-specific meal deals
  // Otherwise, show all meal deals in basket
  const relevantMealDealsInBasket = totalMainTickets > 0 ? instanceMealDealsInBasket : allMealDealsInBasket
  const hasMealDealsInBasket = relevantMealDealsInBasket.length > 0
  
  // In pre-basket mode (reserved seating), track the effective state
  const effectiveMealDealsCount = showBeforeBasket ? 
    (pendingRemoval ? 0 : selectedMealDeals) :
    relevantMealDealsInBasket.length
  

  // Get the default ticket type - prefer "Standard" or first available
  const getDefaultTicketType = () => {
    if (mealDealTicketTypes.ticketOptions.length === 0) return null

    // Try to find a ticket type with "Standard" in the label
    const standardTicket = mealDealTicketTypes.ticketOptions.find(
      option => option.label.toLowerCase().includes('standard')
    )

    // Return Standard if found, otherwise return the first option
    return standardTicket || mealDealTicketTypes.ticketOptions[0]
  }

  // Initialize selected meal deals from basket when in reserved mode
  useEffect(() => {
    if (showBeforeBasket && !initialized && relevantMealDealsInBasket.length > 0) {
      // Initialize to match current main tickets, not basket count
      const initialCount = Math.min(totalMainTickets, relevantMealDealsInBasket.length)
      setSelectedMealDeals(initialCount)
      
      // If we have more meal deals in basket than main tickets, mark excess for removal
      if (relevantMealDealsInBasket.length > totalMainTickets && model) {
        const excessCount = relevantMealDealsInBasket.length - totalMainTickets
        const mealDealsToRemove = relevantMealDealsInBasket.slice(0, excessCount).map(t => t.id)
        model.mealDealsToRemove = mealDealsToRemove
      }
      
      setInitialized(true)
    }
  }, [showBeforeBasket, relevantMealDealsInBasket.length, initialized, totalMainTickets, model])

  // Update selected meal deals when main tickets change
  useEffect(() => {
    if (showBeforeBasket && selectedMealDeals > 0) {
      // Only update if user has already opted in to meal deals
      const expectedCount = Math.min(totalMainTickets, mealDeal?.available || totalMainTickets)
      
      // Update count if tickets were removed
      if (expectedCount < selectedMealDeals && !pendingRemoval) {
        setSelectedMealDeals(expectedCount)
        
        // Update parent when count changes due to ticket removal
        if (mealDeal && mealDealPlan && mealDealTicketTypes.ticketOptions.length > 0) {
          const ticketType = getDefaultTicketType()
          if (ticketType && onMealDealsChange) {
            if (expectedCount === 0) {
              onMealDealsChange(null)
            } else {
              const newMealDealsToAdd = Math.max(0, expectedCount - relevantMealDealsInBasket.length)
              onMealDealsChange({
                count: newMealDealsToAdd,
                mealDeal,
                mealDealPlan: {
                  ...mealDealPlan,
                  seatingPlanId: mealDealPlan.spektrix_v3_id || mealDealPlan.spektrix_id || mealDealPlan.id
                },
                ticketType
              })
            }
          }
        }
      }
      
      // Always check if we need to mark meal deals for removal
      if (relevantMealDealsInBasket.length > totalMainTickets && model) {
        const excessCount = relevantMealDealsInBasket.length - totalMainTickets
        const newMealDealsToRemove = relevantMealDealsInBasket.slice(0, excessCount).map(t => t.id)
        // Only update if different to avoid loops
        if (JSON.stringify(model.mealDealsToRemove) !== JSON.stringify(newMealDealsToRemove)) {
          model.mealDealsToRemove = newMealDealsToRemove
        }
      } else if (relevantMealDealsInBasket.length <= totalMainTickets && model && model.mealDealsToRemove) {
        // Clear removal if we don't have excess
        model.mealDealsToRemove = null
      }
    }
  }, [showBeforeBasket, totalMainTickets, mealDeal?.available, relevantMealDealsInBasket.length, model, pendingRemoval, mealDeal, mealDealPlan, mealDealTicketTypes.ticketOptions.length, onMealDealsChange])
  
  // Auto-adjust meal deals based on model suggestion
  useEffect(() => {
    if (showBeforeBasket && model?.suggestMealDealAdjustment && mealDeal) {
      const suggestion = model.suggestMealDealAdjustment
      
      // Calculate how many meal deals we should have selected
      // This is the existing meal deals PLUS the suggested additional ones
      const targetCount = Math.min(
        suggestion.currentMealDeals + suggestion.suggestedToAdd,
        mealDeal.available || suggestion.currentTickets
      )
      
      // Only auto-select if we haven't already set this count and not pending removal
      if (selectedMealDeals !== targetCount && !pendingRemoval) {
        setSelectedMealDeals(targetCount)
      }
      
      // Always clear the suggestion to prevent loops
      model.suggestMealDealAdjustment = null
    }
  }, [showBeforeBasket, model?.suggestMealDealAdjustment])

  // Notify parent of meal deal changes when in showBeforeBasket mode
  useEffect(() => {
    if (!showBeforeBasket) return
    
    // Handle pending removal
    if (pendingRemoval && relevantMealDealsInBasket.length > 0) {
      // Set meal deals to remove on the model
      if (model) {
        const newMealDealsToRemove = relevantMealDealsInBasket.map(t => t.id)
        // Only update if different
        if (JSON.stringify(model.mealDealsToRemove) !== JSON.stringify(newMealDealsToRemove)) {
          model.mealDealsToRemove = newMealDealsToRemove
        }
      }
      if (onMealDealsChange) {
        onMealDealsChange(null)
      }
    } 
    // Handle meal deal selection
    else if (selectedMealDeals > 0 && mealDeal && mealDealPlan && mealDealTicketTypes.ticketOptions.length > 0) {
      // Don't clear removal flag if we still have excess meal deals
      if (model && relevantMealDealsInBasket.length <= selectedMealDeals && model.mealDealsToRemove) {
        model.mealDealsToRemove = null
      }
      
      const ticketType = getDefaultTicketType()
      if (ticketType && onMealDealsChange) {
        // Calculate how many NEW meal deals to add (not total)
        const newMealDealsToAdd = Math.max(0, selectedMealDeals - relevantMealDealsInBasket.length)
        
        onMealDealsChange({
          count: newMealDealsToAdd,  // Only the additional ones to add
          mealDeal,
          mealDealPlan: {
            ...mealDealPlan,
            seatingPlanId: mealDealPlan.spektrix_v3_id || mealDealPlan.spektrix_id || mealDealPlan.id
          },
          ticketType
        })
      }
    } else {
      // Clear both
      if (model && model.mealDealsToRemove) {
        model.mealDealsToRemove = null
      }
      if (onMealDealsChange) {
        onMealDealsChange(null)
      }
    }
  }, [selectedMealDeals, pendingRemoval, showBeforeBasket, mealDeal, mealDealPlan, mealDealTicketTypes.ticketOptions.length, relevantMealDealsInBasket.length, onMealDealsChange, model])

  // Define handler AFTER all hooks
  const handleToggleMealDeals = async () => {
    
    // In pre-basket mode (reserved seating), handle staging changes
    if (showBeforeBasket) {
      if (relevantMealDealsInBasket.length > 0) {
        // Toggle pending removal
        setPendingRemoval(!pendingRemoval)
        if (!pendingRemoval) {
          // If marking for removal, clear any selected count
          setSelectedMealDeals(0)
          onMealDealsChange(null)
        } else {
          // If unmarking removal, restore the meal deals
          setSelectedMealDeals(relevantMealDealsInBasket.length)
          // Don't update parent here - wait for them to select ticket type
        }
      } else if (selectedMealDeals > 0) {
        // Remove selection
        setSelectedMealDeals(0)
        onMealDealsChange(null)
      } else {
        // Add selection
        const toSelect = Math.min(totalMainTickets, mealDeal.available || 0)
        setSelectedMealDeals(toSelect)
        // Don't update parent here - wait for ticket types to load
      }
      return
    }

    setUpdatingMealDeals(true)

    try {
      if (hasMealDealsInBasket) {
        // Remove relevant meal deal tickets from basket
        const ticketIds = relevantMealDealsInBasket.map(t => t.id)
        
        if (!ticketIds.length || ticketIds.some(id => !id)) {
          throw new Error('Cannot remove tickets - invalid ticket IDs')
        }
        
        await removeTicketsFromBasket(ticketIds)
      } else {
        // Add meal deals matching main ticket quantity
        const ticketType = getDefaultTicketType()

        if (ticketType && mealDealPlan) {
          // Add meal deals matching the quantity of main tickets
          // But don't exceed available quantity
          const toAdd = Math.min(totalMainTickets, mealDeal.available || 0)

          if (toAdd > 0) {
            const ticketsToAdd = []
            for (let i = 0; i < toAdd; i++) {
              ticketsToAdd.push({
                instance: mealDeal.spektrix_v3_id || mealDeal.id,
                seatingPlan: mealDealPlan.spektrix_v3_id || mealDealPlan.spektrix_id || mealDealPlan.id,
                type: ticketType.value
              })
            }

            await addTicketsToBasket(ticketsToAdd)
          }
        }
      }

      // Refresh basket
      if (mutate) mutate()
      if (mutateBasket) mutateBasket()

      // Force a re-fetch after a short delay to ensure the server has updated
      setTimeout(() => {
        if (mutateBasket) mutateBasket()
        setUpdatingMealDeals(false)
      }, 1000)
    } catch (error) {
      alert(`Error updating meal deals: ${error.message || 'Unknown error'}`)
      setUpdatingMealDeals(false)
    }
  }

  // NOW we can do conditional returns - AFTER all hooks have been called

  // Don't show if no meal deals available or none have availability
  if (availableMealDeals.length === 0) {
    return null
  }

  // If showing before basket and no tickets selected yet, show disabled state
  if (showBeforeBasket && totalMainTickets === 0) {
    return (
      <div className='mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg opacity-60'>
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          Select ticket types above to add meal deals
        </p>
      </div>
    )
  }

  // If no tickets for current event and no meal deals in basket at all, don't show anything
  if (!showBeforeBasket && totalMainTickets === 0 && !hasMealDealsInBasket) {
    return null
  }

  if (!mealDeal) return null

  // Show loading state while fetching meal deal data
  if (!mealDealPlan || mealDealTicketTypes.isLoading) {
    return (
      <div className='mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg'>
        <div className='flex items-center justify-center space-x-2 text-gray-600 dark:text-gray-400'>
          <ArrowPathIcon className='w-5 h-5 animate-spin' />
          <span>Loading meal deal options...</span>
        </div>
      </div>
    )
  }

  // Don't show if no ticket types available
  if (mealDealTicketTypes.ticketOptions.length === 0) {
    return null
  }

  return (
    <div className='mt-6 p-4 bg-gradient-to-r from-green-50 to-gray-50 dark:from-gray-700 dark:to-gray-800 rounded-lg border-2 border-green-200 dark:border-green-700'>
      <div className='flex items-center justify-between'>
        <div className='flex-1'>
          <div className='flex items-center space-x-2'>
            <h3 className='font-semibold text-gray-900 dark:text-white'>
              Pre-show Meal Deal
            </h3>
            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'>
              SAVE
            </span>
          </div>
          <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
            {mealDeal.event_name}
          </p>
          {mealDealTicketTypes.ticketOptions.length > 0 && (
            <div className='mt-2'>
              <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                {getDefaultTicketType()?.label.split(' - ')[1] || getDefaultTicketType()?.label}
              </p>
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                {getDefaultTicketType()?.label.split(' - ')[0]}
              </p>
            </div>
          )}
          {(hasMealDealsInBasket || (showBeforeBasket && selectedMealDeals > 0)) && (
            <div className='mt-2 flex items-center space-x-2'>
              <span className={pendingRemoval ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                  {pendingRemoval ? (
                    <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z' clipRule='evenodd' />
                  ) : (
                    <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                  )}
                </svg>
              </span>
              <p className={`text-sm font-medium ${pendingRemoval ? 'text-red-700 dark:text-red-400 line-through' : 'text-green-700 dark:text-green-400'}`}>
                {effectiveMealDealsCount} meal deal{effectiveMealDealsCount !== 1 ? 's' : ''} 
                {showBeforeBasket && !pendingRemoval && selectedMealDeals === 0 && relevantMealDealsInBasket.length > 0 ? ' in basket' : 
                 showBeforeBasket && selectedMealDeals > 0 && relevantMealDealsInBasket.length === 0 ? ' to be added' :
                 showBeforeBasket && pendingRemoval ? ' to be removed' :
                 ' in basket'}
                {getDefaultTicketType()?.ticket?.amount && effectiveMealDealsCount > 0 && !pendingRemoval ?
                  ` - Total: £${(getDefaultTicketType().ticket.amount * effectiveMealDealsCount).toFixed(2)}` : ''}
              </p>
            </div>
          )}
        </div>

        <button
          onClick={handleToggleMealDeals}
          disabled={updatingMealDeals}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg font-medium
            transition-colors disabled:opacity-50 disabled:cursor-not-allowed
            ${(showBeforeBasket && (selectedMealDeals > 0 || pendingRemoval)) || (!showBeforeBasket && relevantMealDealsInBasket.length > 0)
              ? pendingRemoval ? 'bg-amber-300 text-amber-700 hover:bg-amber-400 dark:bg-amber-600 dark:text-amber-300 dark:hover:bg-amber-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
              : 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600'
            }
          `}
        >
          {updatingMealDeals ? (
            <>
              <ArrowPathIcon className='w-5 h-5 animate-spin' />
              <span>Updating...</span>
            </>
          ) : (showBeforeBasket && (selectedMealDeals > 0 || pendingRemoval)) || (!showBeforeBasket && relevantMealDealsInBasket.length > 0) ? (
            <>
              <XMarkIcon className='w-5 h-5' />
              <span>
                {showBeforeBasket && pendingRemoval ? 'Undo Remove' : 
                 showBeforeBasket && selectedMealDeals > 0 ? `Remove ${selectedMealDeals}` :
                 `Remove ${relevantMealDealsInBasket.length}`}
              </span>
            </>
          ) : (
            <>
              <PlusIcon className='w-5 h-5' />
              <span>Add {Math.min(totalMainTickets, mealDeal.available || totalMainTickets)}</span>
            </>
          )}
        </button>
      </div>

      {!hasMealDealsInBasket && totalMainTickets > 0 && (
        <div className='mt-3 pt-3 border-t border-gray-200 dark:border-gray-600'>
          <p className='text-sm text-gray-600 dark:text-gray-400 font-medium'>
            {mealDeal.available < totalMainTickets ? (
              <>⚠️ Limited availability - only {mealDeal.available} meal deal{mealDeal.available !== 1 ? 's' : ''} remaining</>
            ) : (
              <>💡 Save money by adding {Math.min(totalMainTickets, mealDeal.available || totalMainTickets)} meal deal{Math.min(totalMainTickets, mealDeal.available || totalMainTickets) > 1 ? 's' : ''} to match your ticket{totalMainTickets > 1 ? 's' : ''}</>
            )}
          </p>
        </div>
      )}
    </div>
  )
}

export default MealDeals